import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import type { Match, MatchStage, MatchStatus, Player } from './types'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// ---------------------------------------------------------------------------
// Dates. PocketBase stores "2026-08-16 18:00:00.000Z"; JavaScript wants the T.
// ---------------------------------------------------------------------------
export function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null
  if (value instanceof Date) return value
  const parsed = new Date(value.replace(' ', 'T'))
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const formatter = (options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat('en-GB', { timeZone: 'Africa/Cairo', ...options })

const DATE_LONG = formatter({ day: 'numeric', month: 'long', year: 'numeric' })
const DATE_SHORT = formatter({ day: '2-digit', month: 'short' })
const WEEKDAY = formatter({ weekday: 'short' })
const TIME = formatter({ hour: '2-digit', minute: '2-digit', hour12: false })

export const formatDate = (value: string | Date | null | undefined) => {
  const date = toDate(value)
  return date ? DATE_LONG.format(date) : ''
}

export const formatDateShort = (value: string | Date | null | undefined) => {
  const date = toDate(value)
  return date ? DATE_SHORT.format(date).toUpperCase() : ''
}

export const formatWeekday = (value: string | Date | null | undefined) => {
  const date = toDate(value)
  return date ? WEEKDAY.format(date).toUpperCase() : ''
}

export const formatTime = (value: string | Date | null | undefined) => {
  const date = toDate(value)
  return date ? TIME.format(date) : ''
}

/** "in 3 days" / "2 hours ago" — used on fixture cards. */
export function relativeTime(value: string | Date | null | undefined): string {
  const date = toDate(value)
  if (!date) return ''

  const diffMs = date.getTime() - Date.now()
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000_000],
    ['month', 2_592_000_000],
    ['day', 86_400_000],
    ['hour', 3_600_000],
    ['minute', 60_000],
  ]
  const relative = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })

  for (const [unit, ms] of units) {
    if (Math.abs(diffMs) >= ms) return relative.format(Math.round(diffMs / ms), unit)
  }
  return 'now'
}

/** Convert a Date to the "YYYY-MM-DD HH:mm:ss" string PocketBase expects. */
export function toPocketBaseDate(value: string): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString().replace('T', ' ').slice(0, 19)
}

/** Convert a PocketBase date into the value a datetime-local input wants. */
export function toDateTimeLocal(value: string | null | undefined): string {
  const date = toDate(value)
  if (!date) return ''
  const offset = date.getTime() - date.getTimezoneOffset() * 60_000
  return new Date(offset).toISOString().slice(0, 16)
}

export function toDateInput(value: string | null | undefined): string {
  const date = toDate(value)
  return date ? date.toISOString().slice(0, 10) : ''
}

// ---------------------------------------------------------------------------
// Match helpers. Fixtures are stored as neutral home/away pairs, so "are we
// playing, and did we win?" is derived rather than stored.
// ---------------------------------------------------------------------------

/** Loose comparison so "AFC Vikings Cairo" matches "afc vikings cairo ". */
const sameTeam = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase()

export type MatchSide = 'home' | 'away' | null

export function clubSide(match: Match, clubName: string): MatchSide {
  if (sameTeam(match.home_team, clubName)) return 'home'
  if (sameTeam(match.away_team, clubName)) return 'away'
  return null
}

export interface MatchView {
  side: MatchSide
  /** The club's own score, when the club is playing. */
  ourScore: number | null
  theirScore: number | null
  opponent: string
  isHome: boolean
  hasScore: boolean
  result: 'win' | 'draw' | 'loss' | null
  isLive: boolean
  isFinished: boolean
  isUpcoming: boolean
}

export function readMatch(match: Match, clubName: string): MatchView {
  const side = clubSide(match, clubName)
  const isHome = side === 'home'
  const isLive = match.status === 'live' || match.status === 'halftime'
  const isFinished = match.status === 'finished'

  // A PocketBase number field is 0 when empty, not null, so an unplayed fixture
  // would otherwise read as a 0–0 draw. Status is the only reliable signal that
  // a scoreline means anything.
  const hasScore = isLive || isFinished

  const ourScore = side ? (isHome ? match.home_score : match.away_score) : null
  const theirScore = side ? (isHome ? match.away_score : match.home_score) : null

  let result: MatchView['result'] = null
  if (side && isFinished && ourScore !== null && theirScore !== null) {
    if (ourScore > theirScore) result = 'win'
    else if (ourScore < theirScore) result = 'loss'
    else result = 'draw'
  }

  return {
    side,
    ourScore,
    theirScore,
    opponent: side ? (isHome ? match.away_team : match.home_team) : match.away_team,
    isHome,
    hasScore,
    result,
    isLive,
    isFinished,
    isUpcoming: match.status === 'scheduled',
  }
}

const STAGE_LABELS: Record<MatchStage, string> = {
  league: 'League',
  group: 'Group stage',
  'round-of-32': 'Round of 32',
  'round-of-16': 'Round of 16',
  'quarter-final': 'Quarter-final',
  'semi-final': 'Semi-final',
  'third-place': 'Third-place play-off',
  final: 'Final',
  friendly: 'Friendly',
}

export const stageLabel = (stage: MatchStage | '' | undefined) => (stage ? STAGE_LABELS[stage] : '')

const STATUS_LABELS: Record<MatchStatus, string> = {
  scheduled: 'Upcoming',
  live: 'Live',
  halftime: 'Half-time',
  finished: 'Full-time',
  postponed: 'Postponed',
  cancelled: 'Cancelled',
}

export const statusLabel = (status: MatchStatus) => STATUS_LABELS[status] ?? status

// ---------------------------------------------------------------------------
// Players
// ---------------------------------------------------------------------------
export const POSITION_LABELS = {
  goalkeeper: 'Goalkeeper',
  defender: 'Defender',
  midfielder: 'Midfielder',
  forward: 'Forward',
} as const

export const POSITION_ORDER = ['goalkeeper', 'defender', 'midfielder', 'forward'] as const

/** Two-letter monogram used when a player has no portrait uploaded yet. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export function playerAge(player: Pick<Player, 'date_of_birth'>): number | null {
  const born = toDate(player.date_of_birth)
  if (!born) return null
  const diff = Date.now() - born.getTime()
  const age = Math.floor(diff / 31_557_600_000)
  return age > 0 && age < 120 ? age : null
}

// ---------------------------------------------------------------------------
// Loose values
// ---------------------------------------------------------------------------

/**
 * The CMS is schema-driven, so record values arrive typed as `unknown`. This
 * narrows one to something safe to render or put in an input — rather than
 * letting `String()` turn an object into the literal text "[object Object]".
 */
export function asText(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return ''
}

// ---------------------------------------------------------------------------
// Text
// ---------------------------------------------------------------------------

/** Strips HTML so rich text can be used in a meta description or a card. */
export function plainText(html: string, limit = 200): string {
  const text = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&#39;|&rsquo;/g, '’')
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')
    .trim()
  return text.length > limit ? `${text.slice(0, limit - 1).trimEnd()}…` : text
}

export const titleCase = (value: string) =>
  value.replace(/[-_]/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
